import { motion } from 'framer-motion';
import { Search, ArrowRight, TrendingUp, Shield, Users, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS, CATEGORIES, getFeaturedSuppliers, getFeaturedProducts, getFeaturedNews } from '@/lib/index';
import { SUPPLIERS } from '@/data/suppliers';
import { PRODUCTS } from '@/data/products';
import { NEWS_ARTICLES } from '@/data/news';
import { SupplierCard } from '@/components/SupplierCard';
import { ProductCard } from '@/components/ProductCard';
import { NewsCard } from '@/components/NewsCard';
import { AdBanner } from '@/components/AdBanner';
import { SearchBar } from '@/components/SearchBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

export default function Home() {
  const featuredSuppliers = getFeaturedSuppliers(SUPPLIERS);
  const featuredProducts = getFeaturedProducts(PRODUCTS);
  const featuredNews = getFeaturedNews(NEWS_ARTICLES);

  const stats = [
    { icon: Users, label: 'Verified Suppliers', value: '500+' },
    { icon: Shield, label: 'Security Products', value: '2,000+' },
    { icon: Award, label: 'Industry Partners', value: '150+' },
    { icon: TrendingUp, label: 'Countries Served', value: '45+' },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden bg-primary">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.AIRPORT_TERMINAL_5}
            alt="Airport Terminal"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-primary/90 via-primary/60 to-primary/95" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#ffffff', textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}>
              Asia's Leading Airport Security Marketplace
            </h1>
            <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: '#ffffff', textShadow: '1px 1px 6px rgba(0,0,0,0.7)' }}>
              Connect with verified suppliers of cutting-edge security solutions, screening equipment, and aviation technology across Asia Pacific
            </p>
            
            <div className="max-w-3xl mx-auto">
              <SearchBar />
            </div>

            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <Button size="lg" asChild className="bg-white text-primary hover:bg-white/90">
                <Link to={ROUTE_PATHS.SUPPLIERS}>
                  Browse Suppliers
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                <Link to={ROUTE_PATHS.PRODUCTS}>View Products</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="text-3xl font-bold text-foreground mb-2">{stat.value}</div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="text-4xl font-bold mb-4"
            >
              Featured Suppliers
            </motion.h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Discover trusted security solution providers serving airports across Asia Pacific
            </p>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
          >
            {featuredSuppliers.slice(0, 6).map((supplier) => (
              <motion.div key={supplier.id} variants={staggerItem}>
                <SupplierCard supplier={supplier} />
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center">
            <Button size="lg" asChild>
              <Link to={ROUTE_PATHS.SUPPLIERS}>
                View All Suppliers
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="text-4xl font-bold mb-4"
            >
              Product Categories
            </motion.h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Explore comprehensive security solutions across all airport operations
            </p>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {CATEGORIES.map((category) => {
              const iconMap: Record<string, any> = {
                Shield,
                Lock: Shield,
                Camera: Shield,
                Server: Shield,
                Building: Shield,
                AlertTriangle: Shield,
              };
              const Icon = iconMap[category.icon] || Shield;
              
              return (
                <motion.div key={category.id} variants={staggerItem}>
                  <Link to={`${ROUTE_PATHS.PRODUCTS}?category=${category.id}`}>
                    <Card className="h-full hover:shadow-lg transition-all duration-200 hover:scale-[1.02] cursor-pointer">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Icon className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-2">{category.name}</h3>
                            <p className="text-sm text-muted-foreground mb-3">{category.description}</p>
                            <div className="text-sm text-primary font-medium">
                              {category.productCount} Products
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex justify-center mb-12">
            <AdBanner size="leaderboard" position="home-mid" />
          </div>

          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="text-4xl font-bold mb-4"
            >
              Featured Products
            </motion.h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Latest security equipment and technology solutions for modern airports
            </p>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          >
            {featuredProducts.slice(0, 4).map((product) => (
              <motion.div key={product.id} variants={staggerItem}>
                <ProductCard product={product} />
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center">
            <Button size="lg" asChild>
              <Link to={ROUTE_PATHS.PRODUCTS}>
                Browse All Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="text-4xl font-bold mb-4"
            >
              Latest Industry News
            </motion.h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Stay updated with the latest developments in airport security technology
            </p>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12"
          >
            {featuredNews.slice(0, 3).map((article) => (
              <motion.div key={article.id} variants={staggerItem}>
                <NewsCard article={article} />
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center">
            <Button size="lg" variant="outline" asChild>
              <Link to={ROUTE_PATHS.NEWS}>View All News</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={IMAGES.AVIATION_6}
            alt="Aviation Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background/80" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl font-bold mb-6">Stay Informed with Our Newsletter</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Get the latest updates on airport security products, industry news, and exclusive supplier offers delivered to your inbox
            </p>
            
            <Card className="p-8">
              <form className="flex flex-col sm:flex-row gap-4">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1"
                  required
                />
                <Button type="submit" size="lg" className="sm:w-auto">
                  Subscribe Now
                </Button>
              </form>
              <p className="text-sm text-muted-foreground mt-4">
                By subscribing, you agree to receive marketing emails. Unsubscribe anytime.
              </p>
            </Card>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <h2 className="text-3xl font-bold mb-4">List Your Company</h2>
              <p className="text-primary-foreground/90 mb-6">
                Join hundreds of verified suppliers reaching airport security professionals across Asia Pacific. Showcase your products and connect with qualified buyers.
              </p>
              <Button size="lg" variant="secondary" asChild>
                <Link to={ROUTE_PATHS.CONTACT}>Get Started</Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <h2 className="text-3xl font-bold mb-4">Advertise With Us</h2>
              <p className="text-primary-foreground/90 mb-6">
                Reach decision-makers at airports, airlines, and security agencies. Our platform delivers targeted exposure to qualified industry professionals.
              </p>
              <Button size="lg" variant="secondary" asChild>
                <Link to={ROUTE_PATHS.CONTACT}>Learn More</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}